import * as React from 'react';
import { makeStyles } from '@fluentui/react-components';
import { useParams } from 'react-router-dom';
import { HEADER_CONSTANTS } from '../constants/headerConstants';
import { LocationFilter } from '../config/eventsConfig';

interface StickyHeaderProps {
  setActiveSection: (section: 'home' | 'schedule' | 'photos' | 'faq' | 'rsvp' | 'admin') => void;
  scrollToSection: (sectionId: string) => void;
}

const useStyles = makeStyles({
  stickyHeader: {
    position: 'fixed',
    top: 0,
    left: 0,
    width: '100%',
    backgroundColor: 'var(--primary-color)',
    color: '#fff8e1',
    boxShadow: '0 2px 5px rgba(0,0,0,0.15)',
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '0.5rem 1.5rem',
    zIndex: 100,
    height: '50px',
    fontSize: '16px',
    '@media (max-width: 768px)': {
      height: 'auto',
      minHeight: '50px',
      flexDirection: 'column',
      alignItems: 'center',
      padding: '0.5rem 1rem',
    },
  },
  leftSection: {
    display: 'flex',
    alignItems: 'center',
    gap: '1.5rem',
    '@media (max-width: 768px)': {
      flexDirection: 'column',
      gap: '0.5rem',
      marginBottom: '0.5rem',
    },
  },
  logoButton: {
    background: 'none',
    border: 'none',
    color: '#fff8e1',
    fontFamily: 'inherit',
    fontSize: '18px',
    textDecoration: 'none',
  },
  routeIndicator: {
    padding: '0.25rem 0.75rem',
    borderRadius: '20px',
    backgroundColor: 'rgba(255, 248, 225, 0.2)',
    color: '#fff8e1',
    fontSize: '0.85rem',
    fontWeight: '500',
    border: '1px solid rgba(255, 248, 225, 0.3)',
    whiteSpace: 'nowrap',
    '@media (max-width: 768px)': {
      fontSize: '0.8rem',
      padding: '0.2rem 0.6rem',
    },
  },
  navBar: {
    marginRight: '50px',
    display: 'flex',
    flexWrap: 'wrap',
    '@media (max-width: 768px)': {
      marginRight: 0,
      justifyContent: 'center',
      gap: '0.5rem',
      width: '100%',
    },
  },
  navGroup: {
    display: 'flex',
    alignItems: 'center',
    gap: '0.5rem',
    '@media (max-width: 768px)': {
      flexBasis: 'auto',
      justifyContent: 'center',
    },
    '@media (max-width: 480px)': {
      flexWrap: 'wrap',
      gap: '0.3rem',
    },
  },
  navLink: {
    marginLeft: '1.5rem',
    textDecoration: 'none',
    color: '#fff8e1',
    cursor: 'pointer',
    fontSize: 'inherit',
    fontFamily: 'inherit',
    background: 'none',
    border: 'none',
    padding: '0.25rem 0.5rem',
    borderRadius: '4px',
    whiteSpace: 'nowrap',
    '@media (max-width: 768px)': {
      marginLeft: 0,
      fontSize: '14px',
      padding: '0.3rem 0.6rem',
    },
    '@media (max-width: 480px)': {
      fontSize: '13px',
      padding: '0.2rem 0.4rem',
    },
    selectors: {
      '&:hover': {
        textDecoration: 'underline',
        backgroundColor: 'rgba(255, 248, 225, 0.1)',
      },
      '&:focus': {
        outline: '2px solid #fff8e1',
        outlineOffset: '2px',
      },
    },
  },
});

const StickyHeader: React.FC<StickyHeaderProps> = ({ setActiveSection, scrollToSection }) => {
  const classes = useStyles();
  const { location } = useParams<{ location: LocationFilter }>();
  const currentLocation = location || 'all';

  const getRouteLabel = (location: LocationFilter) => {
    switch (location) {
      case 'all': return 'All Events';
      case 'main': return 'Main Events';
      case 'pune': return 'Pune Events';
      case 'mumbai': return 'Mumbai Events';
      default: return 'All Events';
    }
  };

  return (
    <header className={classes.stickyHeader} role={HEADER_CONSTANTS.ARIA_LABELS.BANNER}>
      <div className={classes.leftSection}>
        <div
          className={classes.logoButton}
        >
          {HEADER_CONSTANTS.LOGO_TEXT}
        </div>
        
        <div className={classes.routeIndicator}>
          {getRouteLabel(currentLocation)}
        </div>
      </div>
      
      <nav aria-label={HEADER_CONSTANTS.ARIA_LABELS.PRIMARY_NAV} className={classes.navBar}>
        <div className={classes.navGroup}>
          <button 
            onClick={() => scrollToSection('schedule')} 
            className={classes.navLink}
          >
            {HEADER_CONSTANTS.NAV_LABELS.EVENTS}
          </button>
          <button 
            onClick={() => scrollToSection('photos')} 
            className={classes.navLink}
          >
            {HEADER_CONSTANTS.NAV_LABELS.PHOTOS}
          </button>
        </div>
        <div className={classes.navGroup}>
          <button 
            onClick={() => scrollToSection('faq')} 
            className={classes.navLink}
          >
            {HEADER_CONSTANTS.NAV_LABELS.FAQ}
          </button>
          <button 
            onClick={() => scrollToSection('rsvp')} 
            className={classes.navLink}
          >
            {HEADER_CONSTANTS.NAV_LABELS.RSVP}
          </button>
          <button 
            onClick={() => scrollToSection('admin')} 
            className={classes.navLink}
          >
            {HEADER_CONSTANTS.NAV_LABELS.ADMIN}
          </button>
        </div>
      </nav>
    </header>
  );
};

export default StickyHeader;